"use client";

import { useEffect, useState } from "react";

/**
 * AgentBanner
 *
 * Displays a banner at the top of the screen in development mode
 * showing the current agent name and port. Only visible when NEXT_PUBLIC_AGENT_NAME
 * environment variable is set.
 *
 * Used for multi-agent development workflows to identify which agent is running.
 *
 * The banner pushes content down (non-overlay) to avoid covering UI elements.
 *
 * Customizable via environment variables:
 * - NEXT_PUBLIC_AGENT_NAME: Agent identifier
 * - NEXT_PUBLIC_AGENT_COLOR: Background color (defaults to neutral gray)
 * - NEXT_PUBLIC_AGENT_TEXT_COLOR: Text color (defaults to white)
 */
export function AgentBanner() {
  const [port, setPort] = useState<string>("");

  // Get port after hydration to avoid SSR/client mismatch
  useEffect(() => {
    // Get port from window.location, fallback to 3000 for default dev server
    const detectedPort = window.location.port || "3000";
    setPort(detectedPort);
  }, []);

  // Only show in development
  if (process.env.NODE_ENV !== "development") return null;

  // Only show if agent name is set
  if (!process.env.NEXT_PUBLIC_AGENT_NAME) return null;

  // Use custom colors from env or sensible neutral defaults
  const backgroundColor = process.env.NEXT_PUBLIC_AGENT_COLOR || "#6b7280"; // neutral gray-500
  const textColor = process.env.NEXT_PUBLIC_AGENT_TEXT_COLOR || "#ffffff"; // white

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 border-b border-border-default text-base font-medium"
      style={{
        backgroundColor,
        color: textColor,
      }}
    >
      <span>
        Agent: <strong>{process.env.NEXT_PUBLIC_AGENT_NAME}</strong>
      </span>
      <span className="opacity-70">•</span>
      <span className="opacity-80">Port: {port || "..."}</span>
    </div>
  );
}
